import { Box, Typography } from "@mui/joy";
import { useCallback, useEffect, useRef, useState } from "react";
import { theme } from "styles/theme";

interface AudioProgressBarProps {
  currentTime: number;
  duration: number;
  onSeekStart?: () => void;
  onSeekChange: (newTime: number) => void;
  onSeekEnd: (newTime: number) => void;
  isPlaying: boolean;
  isLive?: boolean;
}

function formatTime(seconds: number): string {
  const s = Math.floor(seconds);
  if (isNaN(s) || s < 0) return "0:00";
  const minutes = Math.floor(s / 60);
  const remainingSeconds = s % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export default function AudioProgressBar({
  currentTime,
  duration,
  onSeekStart,
  onSeekChange,
  onSeekEnd,
  isPlaying,
  isLive = false,
}: AudioProgressBarProps) {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingTime, setDraggingTime] = useState(0);

  const calculateNewTime = useCallback(
    (clientX: number) => {
      if (!progressBarRef.current || duration <= 0) return 0;
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const width = rect.width;
      const seekRatio = Math.max(0, Math.min(1, clickX / width));
      return duration * seekRatio;
    },
    [duration]
  );

  const handleDragStart = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!isPlaying || isLive || duration <= 0) return;

    setIsDragging(true);
    onSeekStart?.();

    const clientX =
      "touches" in event ? event.touches[0].clientX : event.clientX;

    const newTime = calculateNewTime(clientX);
    setDraggingTime(newTime);
    onSeekChange(newTime);
  };

  useEffect(() => {
    const handleDragMove = (event: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      if (event.type === "touchmove") {
        event.preventDefault();
      }

      const clientX =
        'touches' in event
          ? (event as TouchEvent).touches[0].clientX
          : (event as MouseEvent).clientX;

      const newTime = calculateNewTime(clientX);
      setDraggingTime(newTime);
      onSeekChange(newTime);
    };

    const handleDragEnd = (event: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const clientX =
        'touches' in event
          ? (event as TouchEvent).changedTouches[0].clientX
          : (event as MouseEvent).clientX;

      const newTime = calculateNewTime(clientX);
      onSeekEnd(newTime);
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, calculateNewTime, onSeekChange, onSeekEnd]);

  const displayTime = isDragging ? draggingTime : currentTime;

  const progress =
    duration > 0 && !isLive
      ? (Math.min(displayTime, duration) / duration) * 100
      : isLive
        ? 100
        : 0;

  const remainingTime = duration > 0 ? duration - displayTime : 0;

  return (
    <Box sx={{ width: "100%", my: 2 }}>
      <Box
        ref={progressBarRef}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        sx={{
          width: "100%",
          cursor: isPlaying && !isLive && duration > 0 ? "pointer" : "default",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: isDragging ? "16px" : "8px",
            backgroundColor: theme.vars.palette.Surface.Backward,
            borderRadius: isDragging ? "8px" : "4px",
            overflow: "hidden",
            // ✨ 여기가 수정된 부분입니다.
            transition:
              "height 0.2s ease-in-out, border-radius 0.2s ease-in-out",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progress}%`,
              backgroundColor:
                duration > 0
                  ? theme.vars.palette.Content.Highlight
                  : theme.vars.palette.Surface.Backward,
              transition:
                isDragging || isLive
                  ? "none"
                  : "width 0.1s linear, background-color 0.2s ease-in-out",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: "6px",
          px: "3px",
        }}
      >
        <Typography
          variant={"Footnote"}
          textColor={
            isDragging
              ? theme.vars.palette.Content.Highlight
              : theme.vars.palette.Content.Weak
          }
          sx={{
            fontWeight: 700,
            transition: "color 0.2s ease-in-out",
          }}
        >
          {formatTime(duration > 0 ? currentTime : 0)}
        </Typography>
        <Typography
          variant={"Footnote"}
          textColor={
            isDragging
              ? theme.vars.palette.Content.Highlight
              : theme.vars.palette.Content.Weak
          }
          sx={{
            fontWeight: 700,
          }}
        >
          {isLive ? "연속" : `-${formatTime(remainingTime)}`}
        </Typography>
      </Box>
    </Box>
  );
}
